// ContactVerifyDialog.tsx — two-step add/change flow for phone or backup email.
//
// Step 1: destination + current password (re-auth — recovery channels are an
// account-takeover surface). Step 2: 6-digit code entry. When the server has no
// real delivery channel it returns devCode, which we show inline and label
// honestly as the prototype path.
//
// 1.23.50 — the code step used to be a bare text box: focus stayed on the
// primary button when the step opened (so the first keystrokes went nowhere),
// anything could be typed into it, a short entry was posted to the server and
// came back as "That code is wrong or has expired" (it was neither), and the
// only way to act on that advice was to cancel and re-enter the password. The
// step now focuses itself, accepts digits only, refuses to post an incomplete
// code, can fill itself from the prototype code it is already showing, and can
// ask for a fresh one in place. Also 1.23.50: the code is TEXTED to the phone
// on any deploy with TWILIO_* set (backend accounts/sms.py) — devCode, and the
// dashed prototype box below, exist only where nothing can be delivered.
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Dialog, Button, TextField, useToast } from '@/ui';
import { authService } from '@/core/services/authService';
import { ApiError } from '@/core/lib/apiClient';

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Hint = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const DevCode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: color-mix(in srgb, ${({ theme }) => theme.color.accentAmber} 14%, transparent);
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  strong {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.2em;
    /* One click selects the whole code: the letter-spacing that makes it
       readable also makes it fiddly to drag-select digit by digit. */
    user-select: all;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

export type ContactKind = 'phone' | 'backup-email';

/** Verification codes are exactly six digits — see accounts/contact_verification.py. */
const CODE_LENGTH = 6;

/**
 * Codes are digits and nothing else, so anything else is a typo waiting to be
 * posted: a pasted "440 884", a trailing space, a letter from a fat-fingered
 * keypress. Strip it at the edge and the field can only ever hold a valid code
 * or a prefix of one.
 */
function onlyDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

export interface ContactVerifyDialogProps {
  kind: ContactKind;
  open: boolean;
  onClose: () => void;
  /** Called after a successful confirm (session already refreshed). */
  onVerified: () => void;
}

// `sent` describes a code that really went out; `notSent` covers the build with
// no delivery channel wired up, which the server signals by returning the code
// itself (devCode). Claiming "we texted you" while no SMS service exists is
// exactly the lie this dialog used to tell.
const COPY: Record<
  ContactKind,
  { title: string; label: string; placeholder: string; sent: string; notSent: string }
> = {
  'phone': {
    title: 'Add phone number',
    label: 'Phone number',
    placeholder: '+1 555 123 4567',
    sent: 'We texted a 6-digit code to that number. Enter it to confirm.',
    notSent: 'Enter the 6-digit code to confirm this number.',
  },
  'backup-email': {
    title: 'Add backup email',
    label: 'Backup email address',
    placeholder: 'you@somewhere-else.org',
    sent: 'We emailed a 6-digit code to that address. Enter it to confirm.',
    notSent: 'Enter the 6-digit code to confirm this address.',
  },
};

export interface RemoveContactDialogProps {
  kind: ContactKind;
  open: boolean;
  onClose: () => void;
}

/** Password-confirmed removal — stripping a recovery channel is re-authed too. */
export function RemoveContactDialog({ kind, open, onClose }: RemoveContactDialogProps) {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function close() {
    setPassword('');
    setError('');
    onClose();
  }

  async function handleRemove() {
    setBusy(true);
    setError('');
    try {
      if (kind === 'phone') await authService.removePhone(password);
      else await authService.removeBackupEmail(password);
      toast(kind === 'phone' ? 'Phone number removed' : 'Backup email removed');
      close();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'wrong_password'
          ? 'That password is not correct.'
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title={kind === 'phone' ? 'Remove phone number' : 'Remove backup email'}
    >
      <Fields>
        <TextField
          label="Current password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Confirm it's you"
        />
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Fields>
      <Actions>
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleRemove} disabled={busy || !password}>
          {busy ? 'Removing…' : 'Remove'}
        </Button>
      </Actions>
    </Dialog>
  );
}

export function ContactVerifyDialog({ kind, open, onClose, onVerified }: ContactVerifyDialogProps) {
  const toast = useToast();
  const copy = COPY[kind];

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [destination, setDestination] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState('');
  const [devCode, setDevCode] = useState<string | undefined>();
  const [error, setError] = useState('');
  // WHICH request is in flight, not just whether one is: three buttons can be
  // waiting on the network now, and a bare boolean made the resend put
  // "Verifying…" on the Verify button.
  const [busy, setBusy] = useState<null | 'request' | 'verify' | 'resend'>(null);

  const codeRef = useRef<HTMLInputElement>(null);

  // Dialog focuses its own surface once, when it opens, and deliberately never
  // again (its focus effect depends on `open` alone — see ui/Dialog). Stepping
  // to 'confirm' therefore left focus on the primary button, which React reuses
  // across the two steps: the member reads the code, types it, and nothing
  // appears, because a <button> is swallowing the keystrokes. Focus the field
  // the step exists for.
  useEffect(() => {
    if (step === 'confirm') codeRef.current?.focus();
  }, [step]);

  function reset() {
    setStep('request');
    setDestination('');
    setPassword('');
    setCode('');
    setToken('');
    setDevCode(undefined);
    setError('');
  }

  function close() {
    reset();
    onClose();
  }

  /** The one call that issues a code — shared by the first request and the resend. */
  function issueCode() {
    return kind === 'phone'
      ? authService.requestPhoneCode(destination.trim(), password)
      : authService.requestBackupEmailCode(destination.trim(), password);
  }

  async function handleRequest() {
    if (!destination.trim() || !password) {
      setError('Both fields are required.');
      return;
    }
    setBusy('request');
    setError('');
    try {
      const r = await issueCode();
      setToken(r.token);
      setDevCode(r.devCode);
      setStep('confirm');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'wrong_password') {
        setError('That password is not correct.');
      } else if (err instanceof ApiError && err.code === 'validation') {
        setError(
          kind === 'phone'
            ? 'That does not look like a valid phone number.'
            : 'That address cannot be used (check it, and note it must differ from your primary email).',
        );
      } else if (err instanceof ApiError && err.code === 'sms_failed') {
        // The number passed validation but the text did not go out. Said
        // plainly, because the alternative is a member watching a phone that
        // is never going to buzz.
        setError('We could not text a code to that number. Check it, or try again in a moment.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  }

  /** Re-issues the code without making the member re-enter the password. The
   *  old error text told them to "request a new one" with no control to do it —
   *  the only exit was Cancel, which threw the whole re-auth away. */
  async function handleResend() {
    setBusy('resend');
    setError('');
    try {
      const r = await issueCode();
      setToken(r.token);
      setDevCode(r.devCode);
      setCode('');
      // A returned devCode means nothing was delivered — don't claim it was.
      toast(r.devCode ? 'New code ready' : 'New code sent');
      codeRef.current?.focus();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'sms_failed'
          ? 'We could not text a new code to that number. Try again in a moment.'
          : 'Could not send a new code. Please try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  // NOT named `useDevCode` — a use-prefixed function reads as a hook to both
  // the linter and the next person, and this is an ordinary click handler.
  function fillFromDevCode() {
    if (!devCode) return;
    setCode(onlyDigits(devCode));
    setError('');
    codeRef.current?.focus();
  }

  async function handleConfirm() {
    // An incomplete code is not a wrong code. Posting it earned a server
    // round-trip and the flatly untrue "wrong or has expired" — say what is
    // actually the matter, and say how many digits are actually there.
    if (code.length !== CODE_LENGTH) {
      setError(
        code.length === 0
          ? `Enter the ${CODE_LENGTH}-digit code.`
          : `That is only ${code.length} digits. The code is ${CODE_LENGTH}.`,
      );
      codeRef.current?.focus();
      return;
    }
    setBusy('verify');
    setError('');
    try {
      if (kind === 'phone') await authService.confirmPhone(token, code);
      else await authService.confirmBackupEmail(token, code);
      toast(kind === 'phone' ? 'Phone number verified' : 'Backup email verified');
      onVerified();
      close();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'invalid_code'
          ? 'That code is wrong or has expired. Use "Send a new code" to get a fresh one.'
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open={open} onClose={close} title={copy.title}>
      {step === 'request' ? (
        <Fields>
          <TextField
            label={copy.label}
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={copy.placeholder}
          />
          <TextField
            label="Current password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              // Enter submits from the last field, as any form would — the
              // dialog isn't a <form>, so it has to be wired by hand.
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleRequest();
              }
            }}
            placeholder="Confirm it's you"
          />
          <Hint>
            Changing how your account can be recovered requires your password, even while
            signed in.
          </Hint>
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </Fields>
      ) : (
        <Fields>
          <Hint>{devCode ? copy.notSent : copy.sent}</Hint>
          {devCode && (
            <DevCode data-testid="dev-code">
              <span>
                Prototype build. No {kind === 'phone' ? 'SMS service' : 'mailbox'} is connected
                yet, so your code is shown here: <strong>{devCode}</strong>
              </span>
              {/* The code is on screen; making anyone re-type it is how a digit
                  goes missing. One click puts it in the field, exactly. */}
              <Button variant="secondary" size="sm" onClick={fillFromDevCode}>
                Use this code
              </Button>
            </DevCode>
          )}
          <TextField
            ref={codeRef}
            label="6-digit code"
            value={code}
            onChange={(e) => {
              setCode(onlyDigits(e.target.value));
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleConfirm();
              }
            }}
            placeholder="000000"
            maxLength={CODE_LENGTH}
            // inputMode → numeric keypad on mobile web; autoComplete → the OS
            // offers the code from the SMS notification, on a deploy that has
            // TWILIO_* set and really sends one (see accounts/sms.py).
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          {error && <ErrorText role="alert">{error}</ErrorText>}
        </Fields>
      )}

      <Actions>
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        {step === 'request' ? (
          <Button variant="primary" onClick={handleRequest} disabled={busy !== null}>
            {busy === 'request' ? 'Checking…' : 'Continue'}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleResend} disabled={busy !== null}>
              {busy === 'resend' ? 'Sending…' : 'Send a new code'}
            </Button>
            {/* Deliberately NOT disabled on a short code: a dead button explains
                nothing, and handleConfirm's guard says what is missing. */}
            <Button variant="primary" onClick={handleConfirm} disabled={busy !== null}>
              {busy === 'verify' ? 'Verifying…' : 'Verify'}
            </Button>
          </>
        )}
      </Actions>
    </Dialog>
  );
}
